import type { OrgRole, WorkspaceRole } from '@noconflict/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../../shared/api/supabase';
import {
  leaveOrganization,
  listOrgGrants,
  listOrgMembers,
  removeMember,
  revokeWorkspaceGrant,
  setOrgRole,
  setWorkspaceGrant,
  transferOwnership,
} from './api';

export const memberKeys = {
  all: (organizationId: string) => ['members', organizationId] as const,
  list: (organizationId: string) => ['members', organizationId, 'list'] as const,
  grants: (organizationId: string) => ['members', organizationId, 'grants'] as const,
};

export function useOrgMembers(organizationId: string, enabled = true) {
  return useQuery({
    queryKey: memberKeys.list(organizationId),
    queryFn: () => listOrgMembers(supabase, organizationId),
    enabled,
  });
}

export function useOrgGrants(organizationId: string, enabled = true) {
  return useQuery({
    queryKey: memberKeys.grants(organizationId),
    queryFn: () => listOrgGrants(supabase, organizationId),
    enabled,
  });
}

/** Any change to roles or grants may change what the caller themselves can see: refresh the org scope too. */
function useIdentityMutation<TInput>(
  organizationId: string,
  mutationFn: (input: TInput) => Promise<void>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: () =>
      Promise.all([
        qc.invalidateQueries({ queryKey: memberKeys.all(organizationId) }),
        qc.invalidateQueries({ queryKey: ['org-scope'] }),
      ]),
  });
}

export function useSetOrgRole(organizationId: string) {
  return useIdentityMutation(organizationId, (input: { userId: string; role: OrgRole }) =>
    setOrgRole(supabase, organizationId, input.userId, input.role),
  );
}

export function useRemoveMember(organizationId: string) {
  return useIdentityMutation(organizationId, (userId: string) =>
    removeMember(supabase, organizationId, userId),
  );
}

export function useLeaveOrganization(organizationId: string) {
  return useIdentityMutation(organizationId, () => leaveOrganization(supabase, organizationId));
}

export function useTransferOwnership(organizationId: string) {
  return useIdentityMutation(organizationId, (input: { fromUserId: string; toUserId: string }) =>
    transferOwnership(supabase, organizationId, input.fromUserId, input.toUserId),
  );
}

/** `role` null revokes the grant (needs its id); anything else sets or replaces it. */
export function useSetGrant(organizationId: string) {
  return useIdentityMutation(
    organizationId,
    (input: {
      workspaceId: string | null;
      userId: string;
      role: WorkspaceRole | null;
      grantId: string | null;
    }) => {
      if (input.role === null)
        return input.grantId ? revokeWorkspaceGrant(supabase, input.grantId) : Promise.resolve();
      return setWorkspaceGrant(
        supabase,
        { organizationId, workspaceId: input.workspaceId, userId: input.userId },
        input.role,
      );
    },
  );
}
